import { inject, Injectable } from '@angular/core';

import {
  BankTransaction,
  FinancialConnectionsAccount,
  FinancialConnectionsService,
} from './financial-connections.service';
import { FinancialTransaction, PropertyTransactionsService } from './property-transactions.service';
import { FinancialTransactionRulesService, RuleCondition, RuleApplies } from './financial-transaction-rules.service';

const ROWS_PER_PAGE = 200;
const MAX_PAGES_PER_CONNECTION = 5;

export interface BankRowWithConnection extends BankTransaction {
  connectionLabel: string;
}

export interface ApplyRuleToExistingResult {
  updated: number;
  added: number;
  deleted: number;
  skippedMissingProperty: number;
  failed: number;
}

/**
 * Cross-connection helpers shared by the bank rows page and the rule form: the rule
 * preview needs every recent bank row, and telling "in the P&L" apart from "was in
 * the P&L, then deleted" needs the live P&L rows, since a bank row keeps its
 * financial_transaction_id after a delete.
 */
@Injectable({ providedIn: 'root' })
export class BankRowsService {
  private readonly financialConnections = inject(FinancialConnectionsService);
  private readonly propertyTransactions = inject(PropertyTransactionsService);
  private readonly rules = inject(FinancialTransactionRulesService);

  connectionLabel(account: FinancialConnectionsAccount | undefined): string {
    if (!account) { return 'Bank'; }
    return `${account.institution_name} ····${account.last4}`;
  }

  async loadActiveConnections(): Promise<FinancialConnectionsAccount[]> {
    const accounts = await this.financialConnections.getConnectedAccounts().catch(() => []);
    return accounts.filter(a => a.status === 'active');
  }

  async loadRecentBankRows(connections?: FinancialConnectionsAccount[]): Promise<BankRowWithConnection[]> {
    const accounts = connections ?? await this.loadActiveConnections();
    const perConnection = await Promise.all(accounts.map(async account => {
      const rows: BankRowWithConnection[] = [];
      for (let page = 1; page <= MAX_PAGES_PER_CONNECTION; page++) {
        const pageRows = await this.financialConnections
          .getBankTransactions(account.id, { page, per_page: ROWS_PER_PAGE })
          .catch(() => [] as BankTransaction[]);
        rows.push(...pageRows.map(row => ({ ...row, connectionLabel: this.connectionLabel(account) })));
        if (pageRows.length < ROWS_PER_PAGE) { break; }
      }
      return rows;
    }));
    return perConnection.flat().sort((a, b) => (b.date || '').localeCompare(a.date || '') || b.id - a.id);
  }

  /** Live (not deleted) P&L rows keyed by the bank row that produced them. */
  async loadLivePnlRowsByBankRowId(): Promise<Map<number, FinancialTransaction>> {
    const rows = await this.propertyTransactions.getAllFinancialTransactions({ source: 'bank' }).catch(() => []);
    const map = new Map<number, FinancialTransaction>();
    for (const row of rows) {
      if (row.financial_connection_transaction_id) {
        map.set(row.financial_connection_transaction_id, row);
      }
    }
    return map;
  }

  filterMatching<T extends BankTransaction>(rows: T[], conditions: RuleCondition[]): T[] {
    return rows.filter(row => this.rules.matches(row, conditions));
  }

  /**
   * A rule only touches bank rows that arrive after it exists. The backend has no
   * "re-run on history" call, so applying it to the rows the preview matched means
   * walking them here with the endpoints that do exist:
   * - pnl rule: bulk-update rows already in the P&L; add skipped or deleted ones one at a time.
   * - skip rule: delete each live P&L row.
   * - unknown rule: nothing to apply (there is no way to add a bank row without a category).
   */
  async applyRuleToExistingRows(
    applies: RuleApplies,
    matchedRows: BankTransaction[],
    livePnlByBankRowId: Map<number, FinancialTransaction>,
    fallbackAddressId: number | null,
  ): Promise<ApplyRuleToExistingResult> {
    const result: ApplyRuleToExistingResult = { updated: 0, added: 0, deleted: 0, skippedMissingProperty: 0, failed: 0 };
    if (applies.classification === 'unknown') { return result; }

    if (applies.classification === 'skip') {
      for (const row of matchedRows) {
        const live = livePnlByBankRowId.get(row.id);
        if (!live) { continue; }
        try {
          await this.propertyTransactions.deleteFinancialTransaction(live.id);
          result.deleted++;
        } catch {
          result.failed++;
        }
      }
      return result;
    }

    const addressId = applies.address_id ?? fallbackAddressId;
    const typeKey = applies.monthly_finance_type_key;
    const toUpdate = matchedRows.filter(row => livePnlByBankRowId.has(row.id));
    const toAdd = matchedRows.filter(row => !livePnlByBankRowId.has(row.id) && row.classification !== null);

    if (toUpdate.length > 0) {
      try {
        await this.propertyTransactions.updateFinancialTransactions(toUpdate.map(row => ({
          id: livePnlByBankRowId.get(row.id).id,
          ...(typeKey ? { monthly_finance_type_key: typeKey } : {}),
          ...(addressId ? { address_id: addressId } : {}),
        })));
        result.updated += toUpdate.length;
      } catch {
        result.failed += toUpdate.length;
      }
    }

    for (const row of toAdd) {
      if (!typeKey || !addressId) {
        result.skippedMissingProperty++;
        continue;
      }
      try {
        await this.financialConnections.addToProfitAndLoss(row.financial_connection_id, row.id, {
          monthly_finance_type_key: typeKey,
          address_id: addressId,
        });
        result.added++;
      } catch {
        result.failed++;
      }
    }
    return result;
  }
}
